import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteBill, getBill, type BillRecord } from '@/lib/db/billsRepo';
import { useObjectUrl } from '@/hooks/useObjectUrl';

export default function HistoryDetailPage() {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const [bill, setBill] = useState<BillRecord | null | undefined>(undefined);
  const imageUrl = useObjectUrl(bill?.imageFull ?? bill?.imageThumbnail);

  useEffect(() => {
    if (!billId) return;
    void getBill(billId).then(setBill);
  }, [billId]);

  async function handleDelete() {
    if (!billId) return;
    if (!window.confirm('Delete this bill from your history? This cannot be undone.')) return;
    await deleteBill(billId);
    navigate('/history', { replace: true });
  }

  if (bill === undefined) return null;
  if (bill === null) {
    return (
      <div className="p-4 pt-6">
        <p className="text-sm text-slate-500">This bill could not be found — it may have been deleted.</p>
      </div>
    );
  }

  const { billNumber, sellerPan, amount, billDateBs, billDateAd, paymentMethod, sellerName } = bill.billData;

  return (
    <div className="flex flex-col gap-5 p-4 pt-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Bill #{billNumber.value ?? '—'}</h1>
        {sellerName.value && <p className="text-sm text-slate-500">{sellerName.value}</p>}
      </header>

      {imageUrl && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-black">
          <img src={imageUrl} alt="Bill" className="max-h-[40dvh] w-full object-contain" />
        </div>
      )}

      <dl className="grid grid-cols-2 gap-4 rounded-2xl bg-white p-4 text-sm shadow-sm">
        <Detail label="PAN / VAT" value={sellerPan.value} />
        <Detail label="Amount" value={amount.value !== null ? `Rs. ${amount.value.toFixed(2)}` : null} />
        <Detail label="Date (BS)" value={billDateBs.value} />
        <Detail label="Date (AD)" value={billDateAd.value} />
        <Detail label="Payment" value={paymentMethod.value} />
        <Detail label="Status" value={STATUS_LABEL[bill.submissionState]} />
      </dl>

      <div className="flex flex-col gap-3">
        {bill.submissionState !== 'marked_submitted' && (
          <button
            type="button"
            onClick={() => navigate(`/ird/${bill.id}`)}
            className="rounded-2xl bg-blue-700 px-6 py-4 text-base font-semibold text-white active:bg-blue-800"
          >
            Open IRD Assist
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleDelete()}
          className="rounded-2xl border border-red-300 bg-white px-6 py-4 text-base font-medium text-red-700 active:bg-red-50"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<BillRecord['submissionState'], string> = {
  draft: 'Draft',
  ready: 'Ready to submit',
  marked_submitted: 'Submitted (self-reported)',
};

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium capitalize text-slate-900">{value ?? '—'}</dd>
    </div>
  );
}
